// src/components/BoardPack/pages/consolidate_boardpack/Financial_Table_component.jsx
import React from "react";
import { THEME } from "../../constant/color";
import HeadingComponent from "../shared/heading";

const C = THEME.colors;

/* ─────────────────────────────────────────────────────────────
   Shared helpers
   ───────────────────────────────────────────────────────────── */
const fmt = (val) => {
  if (val === null || val === undefined || val === "") return "";
  if (typeof val === "string") return val; // already formatted (e.g. "3.4%")
  const n = Number(val);
  if (Number.isNaN(n)) return val;
  const abs = Math.abs(n).toLocaleString("en-US");
  return n < 0 ? `(${abs})` : abs;
};

const isNegative = (val) => {
  if (typeof val === "number") return val < 0;
  if (typeof val === "string") return val.trim().startsWith("(") || val.trim().startsWith("-");
  return false;
};

// A darker gray than borderColor1 — borderColor1 (#bfbfbf) is nearly the
// same shade as the silver (#C0C0C0) row background, so grid lines on the
// silver cells were rendering with almost no visible contrast.
const BORDER = "#808080";
const BORDER_DARK = C.black;

/* ─────────────────────────────────────────────────────────────
   Layout — column widths (percent of table width)
   label | contribution | 4 (Dec block) | 4 (Dec v PY block) | contribution | 4 (YTD v PY block) | 4 (YTD v Budget block)
   ───────────────────────────────────────────────────────────── */
const LABEL_PCT = 13;
const CONTRIB_PCT = 4;
const DATA_COL_COUNT = 16;
const DATA_PCT = (100 - LABEL_PCT - CONTRIB_PCT * 2) / DATA_COL_COUNT;

/* ─────────────────────────────────────────────────────────────
   Header
   ───────────────────────────────────────────────────────────── */
const HeaderCell = ({ children, widthPct, borderRight, rowSpan }) => (
  <th
    rowSpan={rowSpan}
    style={{
      width: widthPct ? `${widthPct}%` : undefined,
      padding: "2px 2px",
      fontSize: "7px",
      fontWeight: 700,
      textAlign: "center",
      verticalAlign: rowSpan ? "top" : undefined,
      color: C.white,
      backgroundColor: C.blue1,
      borderRight: `1px solid ${borderRight ? BORDER_DARK : "#3a3a8f"}`,
      borderBottom: `1px solid #3a3a8f`,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </th>
);

// 8 value cells (two 4-column blocks) for one half of the table — the
// Contribution column either side of it is rendered separately with
// rowSpan so it reads as one unbroken cell, not three bordered strips.
const HeaderValueCells = ({ values = [], keyPrefix }) => {
  const seg = (start) => values.slice(start, start + 4);
  const renderSeg = (start, segKey) =>
    seg(start).map((v, i) => (
      <HeaderCell key={`${keyPrefix}-${segKey}-${i}`} widthPct={DATA_PCT} borderRight={i === 3}>
        {v}
      </HeaderCell>
    ));

  return (
    <>
      {renderSeg(0, "a")}
      {renderSeg(4, "b")}
    </>
  );
};

const TableHeader = ({ titleLabel, headerYears = [], headerMonths = [], headerMeasures = [] }) => {
  return (
    <thead>
      <tr>
        <th
          rowSpan={2}
          style={{
            width: `${LABEL_PCT}%`,
            backgroundColor: C.white,
            border: `1px solid ${BORDER_DARK}`,
          }}
        />
        <HeaderCell widthPct={CONTRIB_PCT} borderRight rowSpan={3}>
          Contribution
        </HeaderCell>
        <HeaderValueCells values={headerYears.slice(0, 8)} keyPrefix="y1" />
        <HeaderCell widthPct={CONTRIB_PCT} borderRight rowSpan={3}>
          Contribution
        </HeaderCell>
        <HeaderValueCells values={headerYears.slice(8, 16)} keyPrefix="y2" />
      </tr>
      <tr>
        <HeaderValueCells values={headerMonths.slice(0, 8)} keyPrefix="m1" />
        <HeaderValueCells values={headerMonths.slice(8, 16)} keyPrefix="m2" />
      </tr>
      <tr>
        <th
          rowSpan={2}
          style={{
            width: `${LABEL_PCT}%`,
            backgroundColor: C.blue1,
            border: `1px solid ${BORDER_DARK}`,
            borderTop: `0px solid ${BORDER_DARK}`,
            verticalAlign: "bottom",
            textAlign: "left",
            padding: "2px 4px",
            fontSize: "7px",
            fontWeight: 500,
            color: C.white,
          }}
        >
          {titleLabel}
        </th>
        <HeaderValueCells values={headerMeasures.slice(0, 8)} keyPrefix="ms1" />
        <HeaderValueCells values={headerMeasures.slice(8, 16)} keyPrefix="ms2" />
      </tr>
    </thead>
  );
};

/* ─────────────────────────────────────────────────────────────
   Body — value cell (used for the two "Actual/Actual" comparison
   blocks and the two YTD blocks: white base, cream (creamYellow)
   on the Variance / %Variance columns)
   ───────────────────────────────────────────────────────────── */
const ValueCell = ({ value, bg, borderRight, bold }) => (
  <td
    style={{
      textAlign: "center",
      padding: "1px 3px",
      fontSize: "7px",
      fontWeight: bold ? 700 : 400,
      color: isNegative(value) ? C.red : C.black,
      backgroundColor: bg,
      borderRight: `1px solid ${borderRight ? BORDER_DARK : BORDER}`,
      borderBottom: `1px solid ${BORDER}`,
      whiteSpace: "nowrap",
    }}
  >
    {fmt(value)}
  </td>
);

const ContributionCell = ({ value, borderRight }) => (
  <td
    style={{
      textAlign: "center",
      padding: "1px 3px",
      fontSize: "7px",
      fontWeight: 700,
      color: isNegative(value) ? C.red : C.white,
      backgroundColor: C.black,
      borderRight: `1px solid ${borderRight ? BORDER_DARK : "#333"}`,
      borderBottom: `1px solid #333`,
      whiteSpace: "nowrap",
    }}
  >
    {fmt(value)}
  </td>
);

const BodyRow = ({ row, isGrandTotal }) => {
  // Reference report: every data row is uniformly grey (no zebra alternation),
  // and the GRANDTOTAL row's numeric cells are plain white (only the label stays grey).
  const labelBg = C.silver;
  const dec4Bg = isGrandTotal ? C.silver : C.silver; // Actual/Budget block
  const actualPairBg = isGrandTotal ? C.silver : C.silver; // Actual (2025) / Actual (2024) columns
  const varianceBg = isGrandTotal ? C.silver : C.creamYellow; // Variance & %Variance columns

  return (
    <tr>
      <td
        style={{
          padding: "1px 4px",
          fontSize: "7px",
          fontWeight: isGrandTotal ? 700 : 400,
          color: isGrandTotal ? C.red : C.black,
          backgroundColor: labelBg,
          borderLeft: `1px solid ${BORDER_DARK}`,
          borderRight: `1px solid ${BORDER_DARK}`,
          borderBottom: `1px solid ${BORDER}`,
          whiteSpace: "nowrap",
        }}
      >
        {row.label}
      </td>

      <ContributionCell value={row.contribution1} borderRight />

      {/* Dec — Actual vs Budget */}
      <ValueCell value={row.block1?.[0]} bg={dec4Bg} bold={isGrandTotal} />
      <ValueCell value={row.block1?.[1]} bg={dec4Bg} bold={isGrandTotal} />
      <ValueCell value={row.block1?.[2]} bg={varianceBg} bold={isGrandTotal} />
      <ValueCell value={row.block1?.[3]} bg={varianceBg} borderRight bold={isGrandTotal} />

      {/* Dec — Actual vs PY */}
      <ValueCell value={row.block2?.[0]} bg={actualPairBg} bold={isGrandTotal} />
      <ValueCell value={row.block2?.[1]} bg={actualPairBg} bold={isGrandTotal} />
      <ValueCell value={row.block2?.[2]} bg={varianceBg} bold={isGrandTotal} />
      <ValueCell value={row.block2?.[3]} bg={varianceBg} borderRight bold={isGrandTotal} />

      <ContributionCell value={row.contribution2} borderRight />

      {/* YTD — Actual vs PY */}
      <ValueCell value={row.ytd1?.[0]} bg={actualPairBg} bold={isGrandTotal} />
      <ValueCell value={row.ytd1?.[1]} bg={actualPairBg} bold={isGrandTotal} />
      <ValueCell value={row.ytd1?.[2]} bg={varianceBg} bold={isGrandTotal} />
      <ValueCell value={row.ytd1?.[3]} bg={varianceBg} borderRight bold={isGrandTotal} />

      {/* YTD — Actual vs Budget */}
      <ValueCell value={row.ytd2?.[0]} bg={actualPairBg} bold={isGrandTotal} />
      <ValueCell value={row.ytd2?.[1]} bg={actualPairBg} bold={isGrandTotal} />
      <ValueCell value={row.ytd2?.[2]} bg={varianceBg} bold={isGrandTotal} />
      <ValueCell value={row.ytd2?.[3]} bg={varianceBg} borderRight bold={isGrandTotal} />
    </tr>
  );
};

/* ─────────────────────────────────────────────────────────────
   Generic table shell — reused by EBITDATable & NetProfitLossTable
   ───────────────────────────────────────────────────────────── */
const GenericFinancialTable = ({ titleLabel, rows = [], headerYears, headerMonths, headerMeasures }) => {
  return (
    <div style={{ border: `1px solid ${BORDER_DARK}`, width: "100%", overflow: "hidden" }}>
      <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "separate", borderSpacing: 0 }}>
        <TableHeader
          titleLabel={titleLabel}
          headerYears={headerYears}
          headerMonths={headerMonths}
          headerMeasures={headerMeasures}
        />
        <tbody>
          {rows.map((row, idx) => (
            <BodyRow key={idx} row={row} isGrandTotal={!!row.isGrandTotal} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Exported table components — each takes { title, rows, headerYears,
   headerMonths, headerMeasures }, all sourced from the API response.
   ───────────────────────────────────────────────────────────── */
export const EBITDATable = ({ title, rows, headerYears, headerMonths, headerMeasures }) => (
  <GenericFinancialTable
    titleLabel={title}
    rows={rows}
    headerYears={headerYears}
    headerMonths={headerMonths}
    headerMeasures={headerMeasures}
  />
);

export const NetProfitLossTable = ({ title, rows, headerYears, headerMonths, headerMeasures }) => (
  <GenericFinancialTable
    titleLabel={title}
    rows={rows}
    headerYears={headerYears}
    headerMonths={headerMonths}
    headerMeasures={headerMeasures}
  />
);

/* ─────────────────────────────────────────────────────────────
   Full page — title bar + both tables + comments box.
   ───────────────────────────────────────────────────────────── */
const Financial_Table_component = ({
  reportTitle,
  sectionTitle,
  ebitdaTitle,
  ebitdaRows,
  ebitdaHeaderYears,
  ebitdaHeaderMonths,
  ebitdaHeaderMeasures,
  netProfitTitle,
  netProfitRows,
  netProfitHeaderYears,
  netProfitHeaderMonths,
  netProfitHeaderMeasures,
  comments = "",
}) => {
  return (
    <div className="w-full bg-white p-4 flex flex-col gap-1" >
      {/* Header */}
      <div className="relative">
  <div>
    <HeadingComponent variant="main">
      {reportTitle}
    </HeadingComponent>

    <HeadingComponent variant="sub">
      {sectionTitle}
    </HeadingComponent>
  </div>

  <div className="absolute top-3 inset-0 flex justify-end items-start mr-4 pointer-events-none">
    <HeadingComponent variant="cfoSub">
      CFO Report
    </HeadingComponent>
  </div>
</div>

      <EBITDATable
        title={ebitdaTitle}
        rows={ebitdaRows}
        headerYears={ebitdaHeaderYears}
        headerMonths={ebitdaHeaderMonths}
        headerMeasures={ebitdaHeaderMeasures}
      />
      <NetProfitLossTable
        title={netProfitTitle}
        rows={netProfitRows}
        headerYears={netProfitHeaderYears}
        headerMonths={netProfitHeaderMonths}
        headerMeasures={netProfitHeaderMeasures}
      />

      {/* Comments */}
      {/* <div style={{ border: `1px solid ${BORDER_DARK}`, minHeight: "90px" }}>
        <div style={{ fontSize: "13px", fontWeight: 700, textAlign: "center", padding: "4px 6px", color: C.black }}>Comments</div>
        <div style={{ padding: "0 6px 8px", fontSize: "10px", color: "#333", whiteSpace: "pre-wrap" }}>{comments}</div>
      </div> */}
      <div className="mt-1 !border-2 ">
        <div className="border border-black p-2 bg-white">
          <div className="text-center font-semibold text-sm mb-1 border-b border-transparent">
            Comments
          </div>
          <textarea className="w-full h-12 p-2 resize-none outline-none text-sm focus:ring-0 border-0 bg-transparent" />
        </div>
      </div>
    </div>
  );
};

export default Financial_Table_component;